import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import { TreatmentVariablesFormValues } from "./interface";
import useGetVariablesImpactingTreatment from "hooks/api/client/useGetVariablesImpactingTreatment";
import useUpsertVariablesImpactingTreatment from "hooks/api/client/useUpsertVariablesImpactingTreatment";

const EMPTY_VALUES: TreatmentVariablesFormValues = {
  spiritualFactors: "",
  culturalFactors: "",
  legalFactors: "",
  linguisticFactors: "",
  communityResources: "",
};

export const TreatmentVariables: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { variablesImpactingTreatment, refetchVariablesImpactingTreatment } = useGetVariablesImpactingTreatment(clientIdNum);

  const { upsertVariablesImpactingTreatmentMutation, isUpsertVariablesImpactingTreatmentLoading } = useUpsertVariablesImpactingTreatment({
    onSuccess: () => {
      refetchVariablesImpactingTreatment();
      setEditMode(false);
    },
  });

  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState<TreatmentVariablesFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] = useState<TreatmentVariablesFormValues>(EMPTY_VALUES);

  useEffect(() => {
    if (variablesImpactingTreatment) {
      setFormValues(variablesImpactingTreatment);
      setInitialValues(variablesImpactingTreatment);
    }
  }, [variablesImpactingTreatment]);

  const handleChange =
    (field: keyof TreatmentVariablesFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleSave = () => {
    if (!clientIdNum) return;
    upsertVariablesImpactingTreatmentMutation({ clientId: clientIdNum, payload: formValues });
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const saveDisabled =
    JSON.stringify(formValues) === JSON.stringify(initialValues) ||
    isUpsertVariablesImpactingTreatmentLoading;

  return (
    <SectionWrapper
      title="Treatment Variables"
      description="Document spiritual beliefs, cultural practices, legal circumstances, language needs, and community resources that may affect treatment scheduling, care approach, or family support."
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields formValues={formValues} handleChange={handleChange} />
      ) : (
        <ViewFields formValues={formValues} />
      )}
    </SectionWrapper>
  );
};

export default TreatmentVariables;
